import { isCatalogLeafPartId } from '@misumi/shared'
import { useId } from 'react'
import { IconLayers } from '../../../components/icons'
import { buildGroupAccessibleName } from '../staging-bom-accessible-names'
import type {
  BomTreeAssemblyNode,
  BomTreeLeafNode,
  BomTreeLeafRef,
  BomTreeNode,
} from '../staging-bom-tree-view-model'
import {
  collectLeafRefs,
  computeCheckState,
  countDismissedLeaves,
  countRenderableRows,
} from '../staging-bom-tree-view-model'
import type { FileToResolve } from '../staging-types'
import { StagingBomLeafRow, packDisplayFor } from './StagingBomLeafRow'

// PARKED by UID-254 — this row-action prop shape used to be reused directly
// from `StagingReadyToAddTableProps` (that component's own props). UID-254
// retired `StagingReadyToAddTable` (see `staging-bom-tree-view-model.ts`'s
// parked-module note), so the shape is inlined here rather than resurrecting
// the deleted module — same fields, same wiring, no behavior change.
export interface StagingBomRowActionsProps {
  readonly selectedIdentified: ReadonlySet<string>
  /** ADR-0142 D7(1) — `shiftKey` is threaded all the way from the leaf
   * checkbox's own `onChange` event so the unified list's shift-click range
   * selection works INSIDE the tree, not just in the (now-hidden-on-a-BOM-
   * session) flat buckets. A group/header checkbox toggle always passes
   * `false` — a bulk group toggle is not a shift gesture. */
  readonly onToggleSelectIdentified: (partId: string, shiftKey: boolean) => void
  readonly candidateDrawings: readonly FileToResolve[]
  readonly onAssociateDrawing: (partId: string, fileId: string) => void
  readonly onGenerateDrawing: (partId: string) => Promise<unknown>
  readonly onUploadDrawing: (partId: string, file: File) => Promise<unknown>
  /**
   * ADR-0156 D4 (Gate-5 fix) — resolves `true`/`false` (never throws) so
   * `StagingBomLeafRow`'s `QuantityInput` can revert its own draft on a
   * rejected edit. See that component's doc comment.
   */
  readonly onQuantityChange: (partId: string, quantity: number) => Promise<boolean>
  /**
   * ADR-0156 D1 — the node-scoped quantity verb, for the matched/catalog/
   * unresolved arms (`onQuantityChange` above stays `partId`-scoped and is
   * used ONLY by the identified arm, D1.1). Keyed on `nodeId`, NEVER the
   * SKU-derived `partId` — the identical D7 identity hazard
   * `onSelectCatalogOption` below already guards against. Same D4
   * `true`/`false`-resolving contract as `onQuantityChange` above.
   */
  readonly onNodeQuantityChange: (nodeId: string, quantity: number) => Promise<boolean>
  readonly onMaterialChange: (partId: string, material: string | null) => void
  /**
   * UID-497 (T10) — the CUSTOM ("Make") BOM leaf's own drawing + material
   * verbs, all keyed on the row's stable `nodeId`.
   *
   * The four `partId`-scoped drawing callbacks above stay exactly as they are
   * and keep serving the identified arm. These exist because a custom BOM leaf
   * has no `staged_file` row for those to act on — the same split
   * `onNodeQuantityChange` draws against `onQuantityChange`.
   *
   * `onAssociateNodeDrawing` takes a FILENAME, not a `fileId`: the drawing pool
   * on this arm is wider than files-to-resolve (it also holds the parts' own
   * drawings and other leaves' generated ones), and the server re-resolves the
   * name against that pool.
   */
  readonly onAssociateNodeDrawing: (nodeId: string, filename: string) => void
  readonly onDetachNodeDrawing: (nodeId: string) => void
  readonly onGenerateNodeDrawing: (nodeId: string) => Promise<unknown>
  readonly onUploadNodeDrawing: (nodeId: string, file: File) => Promise<unknown>
  readonly onNodeMaterialChange: (nodeId: string, material: string | null) => void
  readonly onRemoveIdentified: (partId: string) => void
  readonly selectedMatched: ReadonlySet<string>
  readonly onToggleSelectMatched: (partId: string, shiftKey: boolean) => void
  /**
   * ADR-0146 D7 — the expanded row's key, which for a BOM-tree leaf is its
   * `nodeId`, NOT its `partId`.
   *
   * A catalog leaf's `partId` is minted FROM its SKU
   * (`mintCatalogLeafPartId`), so it CHANGES whenever the row's resolution
   * does — and keying an open disclosure on it means the panel detaches from
   * its row the first time the sourcing dropdown is used. `nodeId` identifies
   * the BOM POSITION rather than what that position currently resolves to, so
   * it is stable; it is also the key migration 1014 uses for the durable
   * decision, for the same reason. D7 clause 3 explicitly forbids the
   * alternative fix (stabilising `partId`), because its derivability from the
   * SKU is what ADR-0145 D1 built the `cat:` namespace around.
   *
   * Named `RowKey`, not `MatchedId`, deliberately: the old name promised a
   * part id, which is exactly the value that must NOT be passed here.
   * `StagingPage`'s FLAT Matched list still keys on `partId` — those rows have
   * no BOM node — and shares the same single piece of state, so at most one
   * panel is open at a time and the two key spaces cannot collide.
   */
  readonly expandedRowKey: string | null
  readonly onToggleExpandRow: (rowKey: string) => void
  readonly onIgnore: (partId: string) => void
  readonly onUndo: (partId: string) => void
  readonly onSelectOption: (partId: string, optionId: string) => void
  /**
   * ADR-0146 D11 step 5 — the catalog-leaf sourcing dropdown's OWN write
   * path. Keyed on `nodeId` (never `partId` — D7: a catalog leaf's `partId`
   * is minted from its SKU and churns on every selection), distinct from
   * `onSelectOption` above, which stays `partId`-scoped and 404s for a
   * catalog leaf (it has no `staged_file` row, D3).
   */
  readonly onSelectCatalogOption: (nodeId: string, optionId: string) => void
  readonly onKeepUploaded: (partId: string) => void
  readonly onRemoveMatched: (partId: string) => void
  /**
   * ADR-0157 D1/D2 — the catalog leaf's own Remove verb, keyed on the row's
   * stable `nodeId` (never the SKU-derived `partId` — the same identity
   * hazard `onSelectCatalogOption` above already guards against). Never
   * called for a real matched leaf, which keeps `onRemoveMatched` above.
   * `name`/`occurrenceCount` are passed through for the page-level
   * `aria-live` result announcement (the human's spec: multiplicity belongs
   * in the confirmation, not only the button name) — the row itself already
   * has both, so this avoids `StagingPage` re-deriving them from `nodeId`.
   */
  /**
   * ADR-0162 D5/D3.2 (UID-496) — the catalog leaf's node-subject Keep, Ignore
   * and Undo. Keyed on the NODE for the identical reason
   * `onSelectCatalogOption` above is: a catalog leaf's `partId` is minted from
   * its SKU and churns on every selection (ADR-0146 D7's identity hazard), so
   * it is not a stable handle for state about a BOM position.
   */
  readonly onKeepCatalogNode: (nodeId: string, name: string) => void
  /** Gate-5 W7 — `name` and `occurrenceCount` travel so the page can ANNOUNCE
   * the result, the same way `onRemoveCatalogNode` already does. A decision on a
   * deduped row applies to every position it represents, so announcing without
   * the count would understate what happened. */
  readonly onIgnoreCatalogNode: (nodeId: string, name: string, occurrenceCount: number) => void
  readonly onUndoIgnoreCatalogNode: (nodeId: string, name: string, occurrenceCount: number) => void
  readonly onRemoveCatalogNode: (nodeId: string, name: string, occurrenceCount: number) => void
  /** ADR-0157 D3/D2 — dismisses an unresolved leaf (soft, reversible),
   * keyed on `nodeId`. `name`/`bomLine`/`siblingIndex` feed the SAME
   * page-level announcement's "Dismissed BOM line 14, M3x10 screw" copy,
   * built with `formatBomLineLabel` (the identical fallback the row's own
   * visible label uses) rather than a second inline template. */
  readonly onDismissNode: (
    nodeId: string,
    name: string,
    bomLine: number | null,
    siblingIndex: number,
  ) => void
  /** ADR-0157 D3.7 — reverses a dismissal, keyed on `nodeId`. */
  readonly onUndoDismissNode: (nodeId: string) => void
}

export interface StagingBomPartsTreeProps extends StagingBomRowActionsProps {
  readonly tree: readonly BomTreeNode[]
  /** ADR-0142 D2(4) / UX-spec §1.3 — identified/matched parts not referenced
   * by any leaf in `tree`, rendered as flat, top-level, rail-less rows in
   * this SAME `<table>`, after the grouped rows — never a second table, a
   * second section, or a "Loose parts" pseudo-group heading (UX-spec §5.1
   * rules that label OUT for Staging; it belongs to a different screen). */
  readonly ungrouped: readonly BomTreeLeafNode[]
  /**
   * UID-498 — the upload session's human-readable name (`StagingTriage.
   * sessionName`, already surfaced by `StagingPage`'s own session chip, but
   * not previously threaded here). ROOT-ONLY: it names the whole session,
   * not any one assembly, so only the depth-0 header row in `tree` reads it
   * (via {@link deriveRootAssemblyTitle}) — every nested sub-assembly header
   * keeps showing its own `node.name`, untouched. `undefined` (a session
   * with no name, or a non-BOM caller that never had a reason to pass this)
   * is a valid input to that transform, not a special case here.
   */
  readonly sessionName?: string
}

/**
 * ADR-0086 / ADR-0142 §3 — the unified "BOM parts" list. Built on a real,
 * plain semantic `<table>` — NOT `role="treegrid"` (ADR-0136 D5 / UX-spec §6
 * deliberately DROP that role and KEEP the table: real keyboard-operable
 * checkboxes but no APG grid keyboard model — arrow-key row/cell navigation,
 * roving `tabIndex`, `aria-selected` — so a `treegrid` role would be a live
 * WCAG 2.2 4.1.2 "Name, Role, Value" failure the instant it renders).
 * Plain-table ARIA-in-HTML still gives every `<tr>`/`<th>`/`<td>` its
 * implicit `row`/`columnheader`/`gridcell` role for free.
 *
 * ADR-0142 §0/§3.4 — a group row's ONLY interaction is its tri-state
 * selection checkbox. There is no per-group disclosure any more (the
 * prototype has none — `dc.html:8226`'s only `onClick` on a group row is
 * `onGrpToggle`) — `AssemblyGroupRows` below renders `node.children`
 * unconditionally, and neither `aria-level` nor `aria-expanded` is set on a
 * group `<tr>` (both are valid ARIA only inside a `tree`/`treegrid`/`grid`
 * container — axe-verified — so setting them on a plain-table row would be
 * invalid ARIA, worse than omitting it; nesting depth is conveyed visually
 * via the row's leading inset — UID-525 / ADR-0168, previously
 * `.staging-bom-rail`'s width).
 *
 * Selection state: a single `Set<partId>` (owned by the caller, same
 * `selectedIdentified`/`selectedMatched` sets the flat buckets used to use —
 * merged here into one selection surface since a leaf can be either kind).
 * Every group/header checkbox's checked/indeterminate value is *derived*
 * from that set via `computeCheckState` — never separately stored.
 */
export function StagingBomPartsTree({
  tree,
  ungrouped,
  sessionName,
  selectedIdentified,
  selectedMatched,
  onToggleSelectIdentified,
  onToggleSelectMatched,
  candidateDrawings,
  onAssociateDrawing,
  onGenerateDrawing,
  onAssociateNodeDrawing,
  onDetachNodeDrawing,
  onGenerateNodeDrawing,
  onUploadNodeDrawing,
  onNodeMaterialChange,
  onUploadDrawing,
  onQuantityChange,
  onNodeQuantityChange,
  onMaterialChange,
  onRemoveIdentified,
  expandedRowKey,
  onToggleExpandRow,
  onIgnore,
  onUndo,
  onSelectOption,
  onSelectCatalogOption,
  onKeepCatalogNode,
  onIgnoreCatalogNode,
  onUndoIgnoreCatalogNode,
  onKeepUploaded,
  onRemoveMatched,
  onRemoveCatalogNode,
  onDismissNode,
  onUndoDismissNode,
}: StagingBomPartsTreeProps) {
  const headingId = useId()
  const selected = mergeSelection(selectedIdentified, selectedMatched)
  const ungroupedRefs: readonly BomTreeLeafRef[] = ungrouped
    .filter(
      (leaf): leaf is BomTreeLeafNode & { partId: string; partKind: 'identified' | 'matched' } =>
        leaf.partId !== null && leaf.partKind !== null,
    )
    // Gate-5 C-D — an `ungrouped` CATALOG leaf is addressable by no verb at
    // all: its `nodeId` is client-only (no server row), `removeStagedPart`
    // cannot reach a catalog leaf (migration 1013's arm mutex), and a confirm
    // has no BOM position to stamp (ADR-0162 D2.4). `StagingBomLeafRow`
    // disables its per-row checkbox for that reason, so leaving it in this list
    // would let "Select all BOM parts" select a row the user cannot deselect —
    // and drive the header into a `mixed` state that can never be resolved to
    // `checked`.
    //
    // THIS IS A THIRD DOOR, and it is the one that matters. The page's
    // `selectableRowIds`/`visibleSelectableRowIds` carry the same exclusion,
    // but this control is built HERE from `allLeafRefs` and consults neither —
    // which is why an earlier attempt to fix this finding through
    // `selectableRowIds` alone changed nothing that a user could observe.
    .filter((leaf) => !isCatalogLeafPartId(leaf.partId))
    .map((leaf) => ({ partId: leaf.partId, partKind: leaf.partKind }))
  const allLeafRefs = [...tree.flatMap(collectLeafRefs), ...ungroupedRefs]
  const headerState = computeCheckState(
    allLeafRefs.map((ref) => ref.partId),
    selected,
  )

  function toggleAllVisible(nextChecked: boolean) {
    applyGroupSelection(allLeafRefs, nextChecked, selected, {
      onToggleSelectIdentified,
      onToggleSelectMatched,
    })
  }

  return (
    /* UID-525 / ADR-0168 — `table-layout: fixed` gives this table a real
       1036px floor (UID-565: was 1086px), so it needs its own horizontal-
       scroll container rather than relying on `.parts-table`'s shared
       `overflow-x` (which only engages below a 899px media query). */
    <div className="staging-bom-tree-scroll">
      <table className="parts-table staging-bom-tree" aria-labelledby={headingId}>
        <caption id={headingId} className="sr-only">
          BOM parts hierarchy
        </caption>
        {/* UID-525 / ADR-0168 — the comp's fixed column widths
            (`dc.html:3155-3163`). Each fixed `<col>` is the comp's content
            width plus its trailing 16px gap.

            Defect fix (2026-09-11, part 7) — parts 1-6 gave the trailing
            spacer col the table's excess width (first unbounded, then capped
            at 96px), and both attempts rendered as a visible dead stripe
            between "Configuration status" and "Qty" on a wide container —
            capping the SIZE of a still-empty column doesn't change WHERE the
            slack shows up. The spacer `<col>` is kept only because
            `StagingBomLeafRow`'s `<tr>`s render a real `<td />` at this grid
            position (colgroup column count must match) — it is now pinned
            to `width: 0` (`.staging-bom-col-spacer` in uploads.css) so it
            never receives any of the table's excess width itself.
            `.staging-bom-col-name`/`.staging-bom-col-match` take `%` widths
            instead, which — verified empirically against a live Chromium
            render, see that CSS rule's own comment — makes them the ones
            that absorb 100% of any excess above the 1138px fixed-column
            floor, so a wide container grows the two columns that hold real
            variable-length content (name, catalog match/drawing) rather than
            opening a gap anywhere in the row. */}
        <colgroup>
          <col className="staging-bom-col-select" />
          <col className="staging-bom-col-thumb" />
          <col className="staging-bom-col-name" />
          <col className="staging-bom-col-match" />
          <col className="staging-bom-col-type" />
          <col className="staging-bom-col-status" />
          <col className="staging-bom-col-spacer" />
          <col className="staging-bom-col-qty" />
          <col className="staging-bom-col-remove" />
        </colgroup>
        <thead>
          <tr>
            <th scope="col">
              {/* UX-spec §9.3 — WCAG 2.5.8: a bare `<input>` only registers a
                click within its own ~13–16px glyph; padding the `<td>`/`<th>`
                does nothing to the actual hit target. Wrapping in a 24×24
                `<label>` extends the native click/tap target without
                resizing the visible glyph (browsers delegate any pointer
                event landing inside a `<label>` to its `<input>`). */}
              <label className="staging-bom-checkbox-target">
                <input
                  type="checkbox"
                  aria-label="Select all BOM parts"
                  checked={headerState === 'checked'}
                  ref={(el) => {
                    if (el) el.indeterminate = headerState === 'mixed'
                  }}
                  onChange={(event) => toggleAllVisible(event.target.checked)}
                />
              </label>
            </th>
            <th scope="col">
              <span className="sr-only">Thumbnail</span>
            </th>
            <th scope="col">Part name</th>
            <th scope="col">Catalog match / drawing</th>
            <th scope="col">Part type</th>
            <th scope="col">Configuration status</th>
            {/* The comp's `flex:1` spacer (`dc.html:3161`). A `<td>`, NOT a
                `<th>`: it labels nothing, and an empty `<th>` is an
                `empty-table-header` axe violation — caught by this
                component's own axe test. The sibling empty headers
                (thumbnail, remove) are real headers for real columns and
                carry `sr-only` names; a spacer has no name to give. */}
            <td />
            <th scope="col" className="staging-bom-th-qty">
              Qty
            </th>
            <th scope="col">
              <span className="sr-only">Remove</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {tree.map((node) => (
            <BomTreeRows
              key={node.nodeId}
              node={node}
              depth={0}
              ancestorNames={[]}
              isCoverageGap={false}
              // `exactOptionalPropertyTypes` rejects an explicit `undefined`
              // for an optional prop — only spread the key in when present.
              {...(sessionName !== undefined ? { sessionName } : {})}
              selected={selected}
              selectedIdentified={selectedIdentified}
              selectedMatched={selectedMatched}
              onToggleSelectIdentified={onToggleSelectIdentified}
              onToggleSelectMatched={onToggleSelectMatched}
              candidateDrawings={candidateDrawings}
              onAssociateDrawing={onAssociateDrawing}
              onGenerateDrawing={onGenerateDrawing}
              onUploadDrawing={onUploadDrawing}
              onQuantityChange={onQuantityChange}
              onNodeQuantityChange={onNodeQuantityChange}
              onAssociateNodeDrawing={onAssociateNodeDrawing}
              onDetachNodeDrawing={onDetachNodeDrawing}
              onGenerateNodeDrawing={onGenerateNodeDrawing}
              onUploadNodeDrawing={onUploadNodeDrawing}
              onNodeMaterialChange={onNodeMaterialChange}
              onMaterialChange={onMaterialChange}
              onRemoveIdentified={onRemoveIdentified}
              expandedRowKey={expandedRowKey}
              onToggleExpandRow={onToggleExpandRow}
              onIgnore={onIgnore}
              onUndo={onUndo}
              onSelectOption={onSelectOption}
              onSelectCatalogOption={onSelectCatalogOption}
              onKeepCatalogNode={onKeepCatalogNode}
              onIgnoreCatalogNode={onIgnoreCatalogNode}
              onUndoIgnoreCatalogNode={onUndoIgnoreCatalogNode}
              onKeepUploaded={onKeepUploaded}
              onRemoveMatched={onRemoveMatched}
              onRemoveCatalogNode={onRemoveCatalogNode}
              onDismissNode={onDismissNode}
              onUndoDismissNode={onUndoDismissNode}
            />
          ))}
          {/* ADR-0142 D2(4) / UX-spec §1.3/§5.1 — the coverage-diff rows: flat,
            top-level, no pseudo-group heading, rendered in the SAME
            `<tbody>` after the grouped rows, never a second table/section.
            UID-525 / ADR-0168: these get the SAME flat 80px leading inset
            as every other part row (`.staging-row.staging-bom-row >
            td:first-child`), which is what the comp gives every part row on
            both of its builder branches regardless of nesting — so a loose
            leaf and a nested one are no longer inset differently.
            §9.5(b) fix: `isCoverageGap` marks this population so the leaf row
            can show a factual "why no Line N" marker (population (2) —
            identified/matched parts NOT referenced by any `bomHierarchy`
            leaf — is distinct from population (1), a native loose BOM line
            with `assembly: null`, which already has its own `Line N`). */}
          {ungrouped.map((leaf) => (
            <BomTreeRows
              key={leaf.nodeId}
              node={leaf}
              depth={0}
              ancestorNames={[]}
              isCoverageGap={true}
              selected={selected}
              selectedIdentified={selectedIdentified}
              selectedMatched={selectedMatched}
              onToggleSelectIdentified={onToggleSelectIdentified}
              onToggleSelectMatched={onToggleSelectMatched}
              candidateDrawings={candidateDrawings}
              onAssociateDrawing={onAssociateDrawing}
              onGenerateDrawing={onGenerateDrawing}
              onUploadDrawing={onUploadDrawing}
              onQuantityChange={onQuantityChange}
              onNodeQuantityChange={onNodeQuantityChange}
              onAssociateNodeDrawing={onAssociateNodeDrawing}
              onDetachNodeDrawing={onDetachNodeDrawing}
              onGenerateNodeDrawing={onGenerateNodeDrawing}
              onUploadNodeDrawing={onUploadNodeDrawing}
              onNodeMaterialChange={onNodeMaterialChange}
              onMaterialChange={onMaterialChange}
              onRemoveIdentified={onRemoveIdentified}
              expandedRowKey={expandedRowKey}
              onToggleExpandRow={onToggleExpandRow}
              onIgnore={onIgnore}
              onUndo={onUndo}
              onSelectOption={onSelectOption}
              onSelectCatalogOption={onSelectCatalogOption}
              onKeepCatalogNode={onKeepCatalogNode}
              onIgnoreCatalogNode={onIgnoreCatalogNode}
              onUndoIgnoreCatalogNode={onUndoIgnoreCatalogNode}
              onKeepUploaded={onKeepUploaded}
              onRemoveMatched={onRemoveMatched}
              onRemoveCatalogNode={onRemoveCatalogNode}
              onDismissNode={onDismissNode}
              onUndoDismissNode={onUndoDismissNode}
            />
          ))}
        </tbody>
      </table>
    </div>
  )
}

function mergeSelection(
  selectedIdentified: ReadonlySet<string>,
  selectedMatched: ReadonlySet<string>,
): ReadonlySet<string> {
  return new Set([...selectedIdentified, ...selectedMatched])
}

/**
 * Apply a group/header checkbox toggle to every leaf in its scope, routing
 * each leaf's toggle through the callback that owns its flat array
 * (`onToggleSelectIdentified` vs. `onToggleSelectMatched`) per its resolved
 * `partKind` — never guessed from current selection-set membership, which
 * is ambiguous for a not-yet-selected leaf.
 */
function applyGroupSelection(
  leafRefs: readonly BomTreeLeafRef[],
  nextChecked: boolean,
  selected: ReadonlySet<string>,
  callbacks: {
    readonly onToggleSelectIdentified: (partId: string, shiftKey: boolean) => void
    readonly onToggleSelectMatched: (partId: string, shiftKey: boolean) => void
  },
) {
  for (const ref of leafRefs) {
    const isSelected = selected.has(ref.partId)
    if (nextChecked === isSelected) continue
    // A bulk group toggle is never a shift gesture — always `false`.
    if (ref.partKind === 'identified') callbacks.onToggleSelectIdentified(ref.partId, false)
    else callbacks.onToggleSelectMatched(ref.partId, false)
  }
}

interface BomTreeRowsProps {
  readonly node: BomTreeNode
  readonly depth: number
  /** ADR-0136 R1 — ancestor assembly names, immediate parent first,
   * outward; threaded down so a leaf can disambiguate its checkbox/Remove
   * accessible name from the flat bucket row's identical, unqualified one. */
  readonly ancestorNames: readonly string[]
  /** UX-spec §9.5(b) — true only for the `ungrouped` coverage-diff
   * population (identified/matched parts no `bomHierarchy` leaf
   * references); false for every row reached via `tree` (grouped rows AND
   * native top-level loose leaves with a real, if possibly null, `bomLine`).
   * Always `false` for an `AssemblyGroupRows` subtree — the coverage-diff
   * population is never wrapped in an assembly. */
  readonly isCoverageGap: boolean
  /** UID-498 — see `StagingBomPartsTreeProps.sessionName`'s doc comment.
   * Threaded down through every level (via `{...props}` at the recursive
   * call sites) even though only a depth-0 `AssemblyGroupRows` ever reads
   * it — simpler than special-casing the one field that isn't ancestor-
   * scoped like `ancestorNames`. */
  readonly sessionName?: string
  readonly selected: ReadonlySet<string>
  readonly selectedIdentified: ReadonlySet<string>
  readonly selectedMatched: ReadonlySet<string>
  readonly onToggleSelectIdentified: (partId: string, shiftKey: boolean) => void
  readonly onToggleSelectMatched: (partId: string, shiftKey: boolean) => void
  readonly candidateDrawings: StagingBomRowActionsProps['candidateDrawings']
  readonly onAssociateDrawing: StagingBomRowActionsProps['onAssociateDrawing']
  readonly onGenerateDrawing: StagingBomRowActionsProps['onGenerateDrawing']
  readonly onUploadDrawing: StagingBomRowActionsProps['onUploadDrawing']
  readonly onQuantityChange: StagingBomRowActionsProps['onQuantityChange']
  readonly onNodeQuantityChange: StagingBomRowActionsProps['onNodeQuantityChange']
  // UID-497 (T10) — the custom-leaf drawing/material verbs, threaded down the
  // same way every other row action is (see `StagingBomRowActionsProps`).
  readonly onAssociateNodeDrawing: StagingBomRowActionsProps['onAssociateNodeDrawing']
  readonly onDetachNodeDrawing: StagingBomRowActionsProps['onDetachNodeDrawing']
  readonly onGenerateNodeDrawing: StagingBomRowActionsProps['onGenerateNodeDrawing']
  readonly onUploadNodeDrawing: StagingBomRowActionsProps['onUploadNodeDrawing']
  readonly onNodeMaterialChange: StagingBomRowActionsProps['onNodeMaterialChange']
  readonly onMaterialChange: StagingBomRowActionsProps['onMaterialChange']
  readonly onRemoveIdentified: StagingBomRowActionsProps['onRemoveIdentified']
  readonly expandedRowKey: StagingBomRowActionsProps['expandedRowKey']
  readonly onToggleExpandRow: StagingBomRowActionsProps['onToggleExpandRow']
  readonly onIgnore: StagingBomRowActionsProps['onIgnore']
  readonly onUndo: StagingBomRowActionsProps['onUndo']
  readonly onSelectOption: StagingBomRowActionsProps['onSelectOption']
  readonly onSelectCatalogOption: StagingBomRowActionsProps['onSelectCatalogOption']
  readonly onKeepUploaded: StagingBomRowActionsProps['onKeepUploaded']
  readonly onRemoveMatched: StagingBomRowActionsProps['onRemoveMatched']
  readonly onKeepCatalogNode: StagingBomRowActionsProps['onKeepCatalogNode']
  readonly onIgnoreCatalogNode: StagingBomRowActionsProps['onIgnoreCatalogNode']
  readonly onUndoIgnoreCatalogNode: StagingBomRowActionsProps['onUndoIgnoreCatalogNode']
  readonly onRemoveCatalogNode: StagingBomRowActionsProps['onRemoveCatalogNode']
  readonly onDismissNode: StagingBomRowActionsProps['onDismissNode']
  readonly onUndoDismissNode: StagingBomRowActionsProps['onUndoDismissNode']
}

function BomTreeRows(props: BomTreeRowsProps) {
  const { node, depth, ancestorNames, isCoverageGap } = props

  if (node.kind === 'part') {
    if (node.partKind === 'identified' && node.part) {
      const identifiedPart = node.part
      return (
        <StagingBomLeafRow
          kind="identified"
          nodeId={node.nodeId}
          bomLine={node.bomLine}
          depth={depth}
          ancestorNames={ancestorNames}
          isCoverageGap={isCoverageGap}
          occurrenceCount={node.occurrenceCount}
          part={identifiedPart}
          // UID-538 (T5) — the wire node's OWN stated `Process`/`Material`,
          // carried unconditionally by `walk()` (same fields the unresolved
          // arm already reads below). Deliberately NOT threading
          // `node.statedPartType` here — the identified arm's Part Type
          // chip keeps reading `part.type` only (`StagingBomLeafRow`'s
          // `partTypeForChip` doc comment; ADR-0149 OQ-6 is unanswered).
          statedProcess={node.statedProcess}
          statedMaterial={node.statedMaterial}
          selected={props.selectedIdentified.has(identifiedPart.partId)}
          onToggleSelect={(event) =>
            props.onToggleSelectIdentified(identifiedPart.partId, event.shiftKey)
          }
          candidateDrawings={props.candidateDrawings}
          onAssociateDrawing={(fileId) => props.onAssociateDrawing(identifiedPart.partId, fileId)}
          onGenerateDrawing={() => props.onGenerateDrawing(identifiedPart.partId)}
          onUploadDrawing={(file) => props.onUploadDrawing(identifiedPart.partId, file)}
          onQuantityChange={(quantity) => props.onQuantityChange(identifiedPart.partId, quantity)}
          onMaterialChange={(material) => props.onMaterialChange(identifiedPart.partId, material)}
          onRemove={() => props.onRemoveIdentified(identifiedPart.partId)}
        />
      )
    }
    if (node.partKind === 'matched' && node.part) {
      const matchedPart = node.part
      return (
        <StagingBomLeafRow
          kind="matched"
          nodeId={node.nodeId}
          bomLine={node.bomLine}
          depth={depth}
          ancestorNames={ancestorNames}
          isCoverageGap={isCoverageGap}
          occurrenceCount={node.occurrenceCount}
          part={matchedPart}
          // UID-538 (T5) — `IdentifiedLeafProps.statedPartType`'s direct
          // sibling on this arm.
          statedPartType={node.statedPartType}
          selected={props.selectedMatched.has(matchedPart.partId)}
          onToggleSelect={(event) =>
            props.onToggleSelectMatched(matchedPart.partId, event.shiftKey)
          }
          // ADR-0146 D7 — keyed on the NODE, never on the SKU-derived `partId`.
          expanded={props.expandedRowKey === node.nodeId}
          onToggleExpand={() => props.onToggleExpandRow(node.nodeId)}
          onIgnore={() => props.onIgnore(matchedPart.partId)}
          onUndo={() => props.onUndo(matchedPart.partId)}
          onSelectOption={(optionId) => props.onSelectOption(matchedPart.partId, optionId)}
          // ADR-0146 D7/D11 step 5 — the node's OWN id, never `matchedPart.partId`
          // (which churns on every selection, D7's identity hazard).
          onSelectCatalogOption={(optionId) => props.onSelectCatalogOption(node.nodeId, optionId)}
          // Gate-5 W8 — the CUSTOMER'S name for this BOM line (`node.name`), not
          // `matchedPart.name`, which is the CATALOG item's own name for the row
          // it resolved to. Keeping YOUR line while the toast announces the
          // vendor part you just discarded ("Keeping your uploaded SFJ6-20
          // Shoulder Screw") is a different defect from the OQ-C wording
          // question the human ruled on — that ruling was about the STRING, this
          // was the REFERENT. The design's own `revertMatch` names the line too.
          onKeepCatalogNode={() => props.onKeepCatalogNode(node.nodeId, node.name)}
          onIgnoreCatalogNode={() =>
            props.onIgnoreCatalogNode(node.nodeId, node.name, node.occurrenceCount)
          }
          onUndoIgnoreCatalogNode={() =>
            props.onUndoIgnoreCatalogNode(node.nodeId, node.name, node.occurrenceCount)
          }
          onKeepUploaded={() => props.onKeepUploaded(matchedPart.partId)}
          onRemove={() => props.onRemoveMatched(matchedPart.partId)}
          // ADR-0157 D2 — keyed on the node's OWN id, never `matchedPart.
          // partId` (churns on every sourcing selection, D7's identity
          // hazard `onSelectCatalogOption` already guards against).
          onRemoveCatalogNode={() =>
            props.onRemoveCatalogNode(node.nodeId, matchedPart.name, node.occurrenceCount)
          }
          // ADR-0156 D1 — read off the NODE (real matched-file leaf AND
          // catalog leaf both write `staged_bom_node.desired_quantity`),
          // never `matchedPart.quantity` (the flat array's fabricated/stale
          // field, F1). Keyed on `node.nodeId`, never the SKU-derived
          // `partId` — same D7 identity hazard `onSelectCatalogOption`
          // above already guards against.
          capturedQuantity={node.quantity}
          desiredQuantity={node.desiredQuantity}
          onNodeQuantityChange={(quantity) => props.onNodeQuantityChange(node.nodeId, quantity)}
        />
      )
    }
    return (
      <StagingBomLeafRow
        kind="unresolved"
        nodeId={node.nodeId}
        bomLine={node.bomLine}
        depth={depth}
        ancestorNames={ancestorNames}
        isCoverageGap={isCoverageGap}
        // D17.1 — an unresolved leaf is never deduped, so this is always `1`;
        // now passed explicitly (Gate-5 cleanup) rather than relying on a
        // default, per `occurrenceCount`'s own doc comment.
        occurrenceCount={1}
        name={node.name}
        // ADR-0149 Am.1 D19 — the customer's own line-level assertion,
        // carried through unconditionally by `walk()` (D17); `null` renders
        // the row byte-identical to today (D19(4)/D8.3).
        statedPartType={node.statedPartType}
        statedMaterial={node.statedMaterial}
        // UID-517 item 4 (follow-up gap close) — the reused cross-session
        // photo, carried through unconditionally by `walk()` on the same
        // terms as the two stated fields above.
        crossSessionThumbnailUrl={node.crossSessionThumbnailUrl}
        // UID-536 (T3) — `statedMaterial`'s direct sibling, same carry-through.
        statedProcess={node.statedProcess}
        // The line's own `Quantity`, carried on the same terms as the two
        // stated fields above. Read-only on the row, and deliberately NOT
        // folded into `sumGroupQuantity()` — an unresolved leaf still
        // contributes 0 to an assembly's `Qty = N` pill.
        statedQuantity={node.quantity}
        // Gate-5 re-review residual — this leaf's own position among its
        // direct siblings (view-model field, not a new prop threaded down
        // from anywhere else — `node` already carries it), used ONLY as the
        // fallback disambiguator when `bomLine` is `null` on every leaf.
        siblingIndex={node.siblingIndex}
        // ADR-0156 D1 — an unresolved leaf HAS a node (this row's own
        // `nodeId`), just no `partId` resolution, so it takes the SAME
        // node-scoped verb the matched/catalog arms use above.
        desiredQuantity={node.desiredQuantity}
        onNodeQuantityChange={(quantity) => props.onNodeQuantityChange(node.nodeId, quantity)}
        // ADR-0157 D3 — soft-dismiss state and its two callbacks, both keyed
        // on this row's own `nodeId` (the same node-scoped verb the catalog
        // leaf's hard Remove uses; the server decides soft-vs-hard by arm).
        dismissedAt={node.dismissedAt}
        onDismiss={() =>
          props.onDismissNode(node.nodeId, node.name, node.bomLine, node.siblingIndex)
        }
        onUndoDismiss={() => props.onUndoDismissNode(node.nodeId)}
        // UID-497 (T10) — threaded for EVERY unresolved leaf; the ROW renders
        // them only for `statedPartType === 'custom'` (scope (f): a catalog
        // leaf gets no drawing or material control at all). Every callback is
        // curried with this row's own `nodeId`, the same pattern the
        // node-scoped quantity and dismiss verbs above already use.
        candidateDrawings={props.candidateDrawings}
        drawingFilename={node.drawingFilename}
        onAssociateDrawing={(filename) => props.onAssociateNodeDrawing(node.nodeId, filename)}
        onDetachDrawing={() => props.onDetachNodeDrawing(node.nodeId)}
        onGenerateDrawing={() => props.onGenerateNodeDrawing(node.nodeId)}
        onUploadDrawing={(file) => props.onUploadNodeDrawing(node.nodeId, file)}
        selectedMaterial={node.selectedMaterial}
        onMaterialChange={(material) => props.onNodeMaterialChange(node.nodeId, material)}
        selected={false}
        onToggleSelect={() => {}}
      />
    )
  }

  return (
    <AssemblyGroupRows
      node={node}
      depth={depth}
      ancestorNames={ancestorNames}
      isCoverageGap={isCoverageGap}
      // `exactOptionalPropertyTypes` — see the identical comment at the
      // other `BomTreeRows` call site above.
      {...(props.sessionName !== undefined ? { sessionName: props.sessionName } : {})}
      selected={props.selected}
      selectedIdentified={props.selectedIdentified}
      selectedMatched={props.selectedMatched}
      onToggleSelectIdentified={props.onToggleSelectIdentified}
      onToggleSelectMatched={props.onToggleSelectMatched}
      candidateDrawings={props.candidateDrawings}
      onAssociateDrawing={props.onAssociateDrawing}
      onGenerateDrawing={props.onGenerateDrawing}
      onUploadDrawing={props.onUploadDrawing}
      onQuantityChange={props.onQuantityChange}
      onNodeQuantityChange={props.onNodeQuantityChange}
      onAssociateNodeDrawing={props.onAssociateNodeDrawing}
      onDetachNodeDrawing={props.onDetachNodeDrawing}
      onGenerateNodeDrawing={props.onGenerateNodeDrawing}
      onUploadNodeDrawing={props.onUploadNodeDrawing}
      onNodeMaterialChange={props.onNodeMaterialChange}
      onMaterialChange={props.onMaterialChange}
      onRemoveIdentified={props.onRemoveIdentified}
      expandedRowKey={props.expandedRowKey}
      onToggleExpandRow={props.onToggleExpandRow}
      onIgnore={props.onIgnore}
      onUndo={props.onUndo}
      onSelectOption={props.onSelectOption}
      onSelectCatalogOption={props.onSelectCatalogOption}
      onKeepUploaded={props.onKeepUploaded}
      onRemoveMatched={props.onRemoveMatched}
      onKeepCatalogNode={props.onKeepCatalogNode}
      onIgnoreCatalogNode={props.onIgnoreCatalogNode}
      onUndoIgnoreCatalogNode={props.onUndoIgnoreCatalogNode}
      onRemoveCatalogNode={props.onRemoveCatalogNode}
      onDismissNode={props.onDismissNode}
      onUndoDismissNode={props.onUndoDismissNode}
    />
  )
}

function AssemblyGroupRows(
  props: Omit<BomTreeRowsProps, 'node'> & { readonly node: BomTreeAssemblyNode },
) {
  const { node, depth } = props
  const leafRefs = collectLeafRefs(node)
  const checkState = computeCheckState(
    leafRefs.map((ref) => ref.partId),
    props.selected,
  )
  // Bugfix (live manual testing, 2026-09-08) — the "N parts" label below was
  // originally computed off `leafRefs.length` (reused from the checkbox
  // roll-up above, no second walk). `collectLeafRefs` is DELIBERATELY a
  // resolved-leaves-only walk (see its own doc comment in
  // `staging-bom-tree-view-model.ts`) — a `kind: 'part'` leaf with no
  // catalog match (`partId`/`partKind` both null, rendered as "Not included
  // in this upload") contributes nothing to it. A sub-assembly with exactly
  // one such leaf therefore showed "0 parts" even though one part ROW is
  // visibly rendered underneath it — not caught by the original UID-498
  // tests, which only exercised resolved leaves.
  //
  // `countRenderableRows(node)` is the correct predicate for "how many part
  // rows render under this header": its own doc comment draws exactly this
  // distinction, counting every `kind: 'part'` leaf, resolved or not. This
  // IS a second, separate subtree walk (not reusing `leafRefs`) — the same
  // tradeoff this file already makes elsewhere (`sumGroupQuantity` below is
  // its own separate walk too), and the right one: `leafRefs`'s resolved-
  // only semantics must stay untouched for the tri-state checkbox logic
  // above, which is a genuinely different question ("what's selectable")
  // from "what's rendered".
  const renderableRowCount = countRenderableRows(node)
  // UX-spec §9.9.1 / ADR-0156 D2 EXTENSION — the pre-existing "is there a
  // real sum to show" gate, unchanged; see the Qty pill's own comment below
  // for what changed (UID-498) about what happens when this is `false`.
  const hasRealGroupQuantity = leafRefs.length > 0 || hasQuantityBearingUnresolvedLeaf(node)

  function handleGroupToggle(nextChecked: boolean) {
    applyGroupSelection(leafRefs, nextChecked, props.selected, {
      onToggleSelectIdentified: props.onToggleSelectIdentified,
      onToggleSelectMatched: props.onToggleSelectMatched,
    })
  }

  // UID-498 — `isRootHeader` (the SESSION's own top-level header) is
  // deliberately NOT the same thing as `tintVariant` below: `tintVariant`
  // alternates by depth PARITY purely for visual tinting (a depth-2
  // assembly reuses the "root" tint), but there is only ever ONE true root
  // — the depth-0 node — and only it reads `sessionName` for its title. A
  // depth-2 node is still a SUB-assembly for title/sub-line purposes even
  // though it happens to render with the "root" tint.
  const isRootHeader = depth === 0
  const title = isRootHeader ? deriveRootAssemblyTitle(props.sessionName) : node.name
  // UID-498 (b) — a sub-assembly's sub-line defaults its revision segment to
  // "Rev A" when `node.revision` is null (new — the ticket's own ruling
  // scopes this fallback to the sub-assembly case only). The root's sub-line
  // keeps its pre-existing behavior (omit the Rev segment when null) — the
  // ticket's wording never asks for a root fallback, only a sub-assembly one.
  const subLabel = formatAssemblySubLabel(node, { applyRevisionFallback: !isRootHeader })
  const childAncestorNames = [node.name, ...props.ancestorNames]
  // UX-spec §3.1/§4 item 3 — only two group-row tints exist (root/sub); for
  // depth 3+ alternate by depth PARITY rather than minting a third bespoke
  // tint. Even depth (0, 2, 4…) is "root" styling, odd depth (1, 3, 5…) is
  // "sub" styling — reuses only the already-disclosed §3.1 tokens.
  const tintVariant = depth % 2 === 0 ? 'root' : 'sub'

  return (
    <>
      {/* ADR-0142 §0/§3.4 — the group row's ONLY interaction is its
          checkbox; the disclosure `<button>` (and its `useState(expanded)`)
          is DELETED per the human ruling ("no group-row chevrons") — the
          prototype's only `onClick` on a group row is the tri-state
          selection toggle (`dc.html:8226`). Children render unconditionally.
          ADR-0136 D5 correction (axe-verified — `aria-conditional-attr`)
          still applies: `aria-level`/`aria-expanded` are ONLY valid ARIA on
          a `row` inside a `tree`/`treegrid`/`grid` container, so neither is
          set on this plain-table row; nesting depth stays communicated
          visually via the row's leading inset (UID-525 / ADR-0168 — this was
          `.staging-bom-rail`'s width until that ADR moved indentation onto
          the first cell's `padding-left`, where the comp puts it). */}
      <tr
        id={node.nodeId}
        className={`staging-bom-row staging-bom-group-row staging-bom-group-row-${tintVariant}`}
        data-node-id={node.nodeId}
        /* UID-525 / ADR-0168 — the depth ladder's input. Consumed by
           `.staging-bom-group-row > td:first-child`'s
           `calc(24px + var(--bom-depth) * 28px)`, reproducing the comp's
           24px root / 52px sub-assembly insets. */
        style={{ '--bom-depth': depth } as React.CSSProperties}
      >
        {/* UID-525 / ADR-0168 — the checkbox, icon and title share ONE cell
            spanning the Select + Thumbnail + Part name columns, because the
            comp's assembly header is a flat flex row that does NOT align to
            the part rows' columns: it puts its checkbox at the row inset, then
            the icon, then the title, each 12px apart (`dc.html:9432`/`:9438`,
            `gap:12px`), landing the title at 94px while part names sit at
            166px. Keeping the checkbox in its own 114px column instead pushed
            the icon and title out to ~158px — measured, and wrong. */}
        <td colSpan={3}>
          {/* One flex row over checkbox + icon + title, so all three centre
              on the cell and sit at the comp's 12px header gap. */}
          <div className="staging-bom-name-cell staging-bom-group-name-cell">
            {/* UX-spec §9.3 — see the header select-all's identical comment. */}
            <label className="staging-bom-checkbox-target">
              <input
                type="checkbox"
                // UX-spec §9.8.2 item 4 (Finding 7) — thread suffix (B),
                // immediate-parent-only, into the group's OWN label. Uses
                // `props.ancestorNames` (already received, already read once
                // for `childAncestorNames` below) — zero new plumbing.
                aria-label={buildGroupAccessibleName(node.name, props.ancestorNames)}
                checked={checkState === 'checked'}
                ref={(el) => {
                  if (el) el.indeterminate = checkState === 'mixed'
                }}
                onChange={(event) => handleGroupToggle(event.target.checked)}
              />
            </label>
            {/* UID-525 / ADR-0168 — the rail that carried this row's indent is
              GONE; the 24px-base + 28px-per-depth ladder it computed now
              lives on this row's first cell as `padding-left`, which is
              where the comp puts it (`dc.html:9432` root `padding:12px 24px`,
              `:9438` sub `padding:12px 24px 12px 52px`). Same numbers, right
              anchor — as a rail INSIDE this cell the indent was added after
              the checkbox and thumbnail columns instead of subsuming them. */}
            <span
              className={`staging-bom-group-icon staging-bom-group-icon-${tintVariant}`}
              aria-hidden="true"
            >
              {/* UX-spec §3.1 — the 3-plane layers polygon SVG icon, NOT AntD
                `AppstoreOutlined` (a repo-wide grep for `asmCollapse` /
                `bomCollapse` / `collapsedAsm` / `grpCollapse` in the design
                source returns zero hits — `IconLayers` is the exact
                polygon/polyline geometry already hand-inlined for this
                shape family, `icons.tsx:635`). */}
              <IconLayers size={16} />
            </span>
            <span className="parts-row-name staging-bom-group-name">
              <span className="staging-bom-group-title">{title}</span>
              {subLabel && <span className="caption-text">{subLabel}</span>}
              {/* UID-498 (b) — the sub-assembly-only "N parts" count. Root
                gets no count label — the ticket scopes this to sub-assembly
                headers only. Computed via `countRenderableRows(node)`, its
                OWN separate subtree walk — see that constant's own comment
                above for why this can't reuse `leafRefs` (the resolved-
                leaves-only walk the checkbox/pill logic needs). */}
              {!isRootHeader && (
                <span className="caption-text">
                  {renderableRowCount} part{renderableRowCount === 1 ? '' : 's'}
                </span>
              )}
            </span>
          </div>
        </td>
        {/* Catalog match / Part type / Configuration status — a group
            header fills none of them. */}
        <td />
        <td />
        <td />
        {/* UID-525 / ADR-0168 — the comp's `flex:1` spacer column. */}
        <td />
        <td className="staging-bom-group-qty">
          {/* UX-spec §9.4 (Gate-5 fix) — dc.html's own `Qty = 1` literal
              (`:9431`, `:9437`) is a hardcoded fixture placeholder, never a
              real aggregate: EVERY header there shows the same literal
              string regardless of member count. This must compute an actual
              sum over the group's OWN children, always — never `node.
              quantity` (the wire's per-BOM-LINE quantity for THIS assembly
              node, never a rollup).

              UX-spec §9.9.1 (Gate-5, superseding the interim "suppress when
              sum === 0" call) — gating on the SUM's value was wrong: it
              conflates "there are no quantities to sum" with "the sum is
              genuinely zero", and would hide a TRUE zero on an assembly that
              does have resolved leaves. The correct gate is whether this
              subtree has ANY resolved leaf at all —
              `collectLeafRefs(node).length > 0` — reusing `leafRefs`, already
              computed above for `checkState`, rather than a second walk. An
              all-unresolved assembly (kept by `pruneEmptyAssemblies` per
              D3(iii)) shows no pill at all; any assembly with a resolved leaf
              shows its true sum, genuine 0 included.

              Gate-5 cleanup — `sumGroupQuantity(node)` is a full subtree
              walk (the third per group row, alongside `collectLeafRefs` and
              `computeCheckState`), so it now only runs when the pill is
              actually shown, not unconditionally on every render.

              ADR-0156 D2 EXTENSION — the gate ALSO fires when a subtree has
              NO resolved leaf but DOES have an unresolved one that
              contributes a quantity (`hasQuantityBearingUnresolvedLeaf`).
              Without this, "a quantity that rolls up" would be true in the
              number but invisible in the UI.

              WIDENED by the 2026-09-07 amendment: this used to require an
              EDITED leaf, and explicitly left a captured-only all-unresolved
              assembly with NO pill, on §9.9.1's reasoning that its sum was a
              meaningless 0. That premise is gone — captured quantities now
              count, so such an assembly has a real total (and its leaves each
              display a solid number, so a parent showing nothing would
              contradict them). An assembly whose unresolved leaves carry no
              quantity at all still shows no pill, which is what keeps
              §9.9.1's actual concern — never fabricating a "Qty = 0" —
              intact.

              UID-498 RULING (2026-09-08, refines ADR-0156 D2/§9.9.1 without
              reopening either): the gate above used to mean "no pill at all"
              when it failed. It no longer does — a HUMAN ruling on this
              ticket found that hiding the pill entirely reads as a broken/
              blank cell, not as "nothing to sum". The gate is UNCHANGED and
              still decides one thing: whether there is a REAL sum to trust.
              When it's true, `sumGroupQuantity(node)` renders exactly as
              before (genuine 0 included, never suppressed — §9.9.1's
              surviving half). When it's false — a flat group with no
              children, or one whose children carry no stated quantity
              anywhere — the pill now shows a DEFAULT `1` instead of vanishing,
              never a fabricated computed value. This is the one hardcoded
              literal this component has: the design's own `Qty = 1`
              (`dc.html:9431`, `:9437`) the rest of this comment thread
              debunked as a fixture placeholder is, for exactly this one
              case, the right floor after all — never fewer than 1, same
              historical floor `sumGroupQuantity`'s `matched` arm already
              uses. */}
          <span>Qty = {hasRealGroupQuantity ? sumGroupQuantity(node) : 1}</span>
        </td>
        <td />
      </tr>
      {node.children.map((child) => (
        <BomTreeRows
          key={child.nodeId}
          {...props}
          node={child}
          depth={depth + 1}
          ancestorNames={childAncestorNames}
        />
      ))}
    </>
  )
}

/**
 * UX-spec §9.4 — a group header's qty pill sums only ITS OWN descendants'
 * quantities, recursively, with zero leakage from a sibling assembly that
 * happens to reference the same `partId` at another position (ADR-0142
 * D13(4) forbids aggregating a part's quantity ACROSS positions — this is
 * the opposite operation: summing DIFFERENT parts' own quantities WITHIN one
 * position's subtree, which D13 never restricts).
 *
 * **ADR-0156 D2 — the rollup rule, NARROWED not deleted.** A captured
 * quantity on a RESOLVED leaf (identified/matched/catalog) still rolls up
 * unconditionally — D13(4) never restricted that, and this is a
 * PRE-EXISTING behavior D2 deliberately leaves alone (see R2: a catalog
 * leaf's captured quantity already rolled up before this ADR; identified
 * always has, via `part.quantity`, D1.1). An UNRESOLVED leaf's quantity
 * rolls up ONLY once a human has typed a desired value — the whole reason
 * the pre-ADR-0156 divergence existed (a stated value is one line's
 * unverified claim; a group pill that summed it would present a total this
 * app cannot stand behind until the line resolves) STOPS APPLYING the
 * moment the number is the app's own:
 *
 *   identified        `part.quantity`                        — unchanged
 *   matched / catalog `node.desiredQuantity ?? node.quantity ?? 1` — reads
 *                      the NODE now (F1: `part.quantity` was a hard-coded/
 *                      stale fabrication), same desired-preferred-over-
 *                      captured read `StagingBomLeafRow`'s `QuantityInput`
 *                      renders, falling back to `1` only if BOTH are absent
 *                      (the historical fabricated floor, never fewer than 1)
 *   unresolved        `node.desiredQuantity ?? 0`             — captured
 *                      alone still contributes 0; a DESIRED value counts
 *
 * Renamed/extended test coverage (both halves of the divergence):
 * `StagingBomPartsTree.stated-quantity-no-rollup.test.tsx`.
 */
/**
 * ADR-0156 D2 EXTENSION, WIDENED by the 2026-09-07 amendment — see the
 * pill-visibility gate's own comment above (`AssemblyGroupRows`). `true` iff
 * this subtree contains an UNRESOLVED leaf that CONTRIBUTES a quantity: the
 * case `collectLeafRefs` (RESOLVED-only) cannot see, but which must still
 * make the pill visible.
 *
 * This used to require `desiredQuantity !== null` — an EDITED leaf — because
 * a merely-captured quantity contributed 0 to the sum, so a pill would have
 * read "Qty = 0" and said nothing true. The amendment makes a captured
 * quantity count, so `quantity` (captured) now also qualifies: an assembly
 * whose leaves each display a solid number off the sheet must show their
 * total, or the leaves and their parent contradict each other on screen.
 *
 * Still deliberately EXCLUDED, so the pill never fabricates a total:
 *   - a leaf with NEITHER a desired nor a captured quantity (nothing to sum);
 *   - a DISMISSED leaf (ADR-0157 D3.5 — its contribution is removed from the
 *     sum, so it must not be what makes the pill appear either).
 */
function hasQuantityBearingUnresolvedLeaf(node: BomTreeNode): boolean {
  if (node.kind === 'part') {
    return (
      node.partKind === null &&
      node.dismissedAt === null &&
      (node.desiredQuantity !== null || node.quantity !== null)
    )
  }
  return node.children.some(hasQuantityBearingUnresolvedLeaf)
}

function sumGroupQuantity(node: BomTreeNode): number {
  if (node.kind === 'part') {
    if (node.partKind === 'identified') return node.part.quantity
    if (node.partKind === 'matched') {
      // UID-494 (T7) code-review C2 fix — this rollup must count the SAME
      // effective quantity `StagingBomLeafRow`'s `QuantityInput` displays,
      // or a packed leaf reading "1" while its assembly's `Qty = N` pill
      // still added the raw pre-pack number would contradict the very leaf
      // sitting under it (the exact "user reads the pill as a bug" outcome
      // the 2026-09-07 ruling above was written to prevent). A human's
      // `desiredQuantity` still wins outright, unaffected by packing.
      if (node.desiredQuantity !== null) return node.desiredQuantity
      const packDisplay = packDisplayFor(node.part.name, node.quantity)
      return packDisplay ? packDisplay.displayQuantity : (node.quantity ?? 1)
    }
    // ADR-0157 D3.5 — a dismissed leaf's contribution is REMOVED regardless
    // of whether it was ever edited (a never-edited dismissed leaf was
    // already contributing 0, so this changes nothing for that case; an
    // edited-then-dismissed leaf's contribution drops out, which IS a
    // visible pill change — the whole point of dismissal withdrawing the
    // line).
    // HUMAN RULING (2026-09-07) — AMENDS ADR-0156 D2's provenance rule, and
    // resolves its own R5 ("PM call, NOT an architect call ... route to pm +
    // product-designer for whether the un-edited state needs a visual
    // affordance (placeholder vs value)").
    //
    // D2 originally read `desiredQuantity ?? 0`: a leaf whose quantity was
    // only ever READ OFF THE SHEET contributed 0 to this pill, so `Qty = N`
    // meant "confirmed-by-a-human only". That was coupled to rendering the
    // captured number as a PLACEHOLDER (D2's "MUST NOT render a captured
    // quantity as a filled input value") — the grey placeholder is what made
    // the exclusion legible instead of looking like a bug.
    //
    // The design (`Fictiv Platform.dc.html:3235`, re-measured off the running
    // prototype) renders that number as a SOLID `value`, and the ruling
    // adopts the design. Once the leaf shows a solid `4`, an assembly total
    // that excludes it IS the "user reads the pill as a bug" outcome R5
    // warned about — so the rollup has to move with it. The pill's meaning
    // becomes "as read off your sheet, plus anything since edited", which is
    // what a solid value already promises the reader.
    //
    // `capturedQuantity` is second, never first: a human's `desiredQuantity`
    // still wins outright. `?? 0` remains the floor for a leaf with neither.
    return node.dismissedAt !== null ? 0 : (node.desiredQuantity ?? node.quantity ?? 0)
  }
  return node.children.reduce((sum, child) => sum + sumGroupQuantity(child), 0)
}

function formatAssemblySubLabel(
  node: BomTreeAssemblyNode,
  options: {
    /** UID-498 (b) — when `true` (sub-assembly headers only, per the
     * ticket's own wording: only the sub-assembly case names "Rev A" as a
     * fallback), a `null` revision renders as "Rev A" instead of omitting
     * the segment. The root header's pre-existing behavior (omit when null)
     * is preserved by passing `false` at that call site — the ticket never
     * asks for a root fallback, only a sub-assembly one. */
    readonly applyRevisionFallback: boolean
  },
): string | null {
  const parts: string[] = []
  if (node.revision) {
    parts.push(`Rev ${node.revision}`)
  } else if (options.applyRevisionFallback) {
    parts.push('Rev A')
  }
  // D4 — buildCount's semantics are undefined; omit the "· N builds" segment
  // entirely when null rather than inventing a meaning (never render "· null
  // builds" or default to 0/1).
  if (node.buildCount !== null)
    parts.push(`${node.buildCount} build${node.buildCount === 1 ? '' : 's'}`)
  // ADR-0157 D3.5 — "All N lines dismissed" appended ONLY when every leaf in
  // this subtree is dismissed (an all-unresolved assembly is KEPT by design,
  // ADR-0136 D3(iii) — the assembly header stays, this is the only
  // acknowledgement it gets; no empty-state card, per the human's spec).
  // `total > 0` guards the vacuous case (already unreachable in practice —
  // `pruneEmptyAssemblies` removes a zero-leaf assembly before this ever
  // runs — but the guard is explicit rather than relying on that invariant
  // silently).
  const { total, dismissed } = countDismissedLeaves(node)
  if (total > 0 && dismissed === total) {
    parts.push(`All ${total} line${total === 1 ? '' : 's'} dismissed`)
  }
  return parts.length > 0 ? parts.join(' · ') : null
}

/**
 * UID-498 (a) — the root header's title, derived from the upload session's
 * own name (`StagingTriage.sessionName`) rather than the tree's `node.name`
 * (which, for the top-level wire node, is whatever the spreadsheet's own
 * hierarchy happened to call it — not what the ticket wants shown). No
 * title-transform utility existed anywhere in the repo before this; written
 * fresh here and unit-tested directly against the ticket's own worked
 * examples.
 *
 * Steps, in order (the ticket's own words):
 *   1. Strip the file extension (last `.ext`, if any).
 *   2. Replace every run of `_`/`-` with a single space.
 *   3. Remove the standalone word "bom" (case-insensitive, word-bounded —
 *      never chews into "bombshell" or similar).
 *   4. Collapse the whitespace that (2)/(3) can leave behind and trim.
 *   5. Title-case each remaining word — capitalize the first character,
 *      lowercase the rest (so `"v2"` → `"V2"`, `"No000066"`'s existing
 *      capital `N` survives lowercasing the rest of a token that already
 *      starts uppercase).
 *
 * One refinement beyond the ticket's literal four steps, required to match
 * its OWN worked example: a token with no letters at all (e.g. a browser's
 * `" (1)"` duplicate-download suffix) carries no naming information, so it
 * is dropped in step 5 rather than title-cased into a bare `"(1)"` — this is
 * what turns `"No000066_partslist (1).csv"` into `"No000066 Partslist"`
 * (not `"No000066 Partslist (1)"`) per the ticket's exact acceptance
 * criterion. A token that MIXES letters and digits (`"No000066"`, `"v2"`) is
 * real naming information and is kept.
 *
 * Falls back to `"Assembly"` when every step above leaves nothing (an absent
 * `sessionName`, or one that transforms to empty — e.g. a filename made
 * entirely of separators/extension).
 */
export function deriveRootAssemblyTitle(sessionName: string | null | undefined): string {
  const withoutExtension = (sessionName ?? '').replace(/\.[^./\\]+$/, '')
  const withoutSeparators = withoutExtension.replace(/[_-]+/g, ' ')
  const withoutBom = withoutSeparators.replace(/\bbom\b/gi, ' ')
  const collapsed = withoutBom.replace(/\s+/g, ' ').trim()
  const words = collapsed
    .split(' ')
    .filter((word) => word.length > 0 && /[a-zA-Z]/.test(word))
    .map((word) => word.slice(0, 1).toUpperCase() + word.slice(1).toLowerCase())
  return words.length > 0 ? words.join(' ') : 'Assembly'
}
